import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { installChunkReload } from './chunkReload'
import { tokenStore } from '@/api/http'
import { redirectQueryOf } from './redirect'
import { useAuthStore } from '@/stores/auth'
import { clearSession } from '@/stores/session'
import { t } from '@/locales'
import type { Capability } from '@/constants/capabilities'

/**
 * 静态路由表 + 能力守卫。
 *
 * `meta.cap` 用的是**接口文档标注的那个能力标签名**，与后端 portal 闸同一套词，
 * 联调时对得上（R-10 双重防护：前端隐藏入口 + 接口层拒绝）。
 *
 * 特别注意 R-17：待提交状态下通过深链、邮件旧链接或直接输入地址访问
 * 知识测试 / 身份认证页面，**必须拦回概览并弹「请先完成开户申请」弹窗**。
 * 这条就是靠下面的守卫 + capsOf() 里 PENDING_SUBMIT 不含 activation 实现的。
 */
declare module 'vue-router' {
  interface RouteMeta {
    public?: boolean
    blank?: boolean
    titleKey?: string
    cap?: Capability
    /** 报表页共用组件时区分两张表 */
    kind?: 'deposit' | 'withdrawal'
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    component: () => import('@/layouts/SplitLayout.vue'),
    children: [
      {
        path: '',
        name: 'login',
        component: () => import('@/views/auth/LoginView.vue'),
        meta: { public: true, blank: true, titleKey: 'auth.loginTitle' },
      },
    ],
  },
  {
    /**
     * 公司相关人士的认证链接落地页（V1.4 §6A）。**公开**：收信的董事 / UBO 在我们这里
     * 没有账号，唯一的凭据就是链接里的 token。
     */
    path: '/kyc/invite/:token',
    component: () => import('@/layouts/BlankLayout.vue'),
    children: [
      {
        path: '',
        name: 'kyc-invite',
        component: () => import('@/views/activation/KycInviteView.vue'),
        meta: { public: true, blank: true, titleKey: 'kycInvite.title' },
      },
    ],
  },
  {
    // 准入被拒 under_review 的落地页（V1.4 §5.4）。公开：被拒时不签 token，这一页拿不到登录态
    path: '/review-pending',
    component: () => import('@/layouts/BlankLayout.vue'),
    children: [
      {
        path: '',
        name: 'review-pending',
        component: () => import('@/views/auth/ReviewPendingView.vue'),
        meta: { public: true, blank: true, titleKey: 'auth.reviewPendingTitle' },
      },
    ],
  },
  {
    path: '/forgot-password',
    component: () => import('@/layouts/BlankLayout.vue'),
    children: [
      {
        path: '',
        name: 'forgot-password',
        component: () => import('@/views/auth/ForgotPasswordView.vue'),
        meta: { public: true, blank: true, titleKey: 'auth.forgotTitle' },
      },
    ],
  },
  {
    path: '/reset-password',
    component: () => import('@/layouts/BlankLayout.vue'),
    children: [
      {
        path: '',
        name: 'reset-password',
        component: () => import('@/views/auth/ResetPasswordView.vue'),
        meta: { public: true, blank: true, titleKey: 'auth.resetTitle' },
      },
    ],
  },
  {
    // 接口 7.0：MT5 交易密码重置邮件的落地页。公开端点，客户常在没登录的设备上点开邮件
    path: '/mt5-password/reset',
    component: () => import('@/layouts/BlankLayout.vue'),
    children: [
      {
        path: '',
        name: 'mt5-password-reset',
        component: () => import('@/views/auth/Mt5PasswordResetView.vue'),
        meta: { public: true, blank: true, titleKey: 'mt5pwd.resetTitle' },
      },
    ],
  },
  {
    // R-18 密码已过期的全屏强制改密页：除退出登录与帮助与支持外不可绕过
    path: '/change-password',
    component: () => import('@/layouts/BlankLayout.vue'),
    children: [
      {
        path: '',
        name: 'change-password',
        component: () => import('@/views/auth/ChangePasswordView.vue'),
        meta: { blank: true, titleKey: 'auth.changeTitle' },
      },
    ],
  },

  {
    path: '/',
    component: () => import('@/layouts/PortalLayout.vue'),
    children: [
      {
        path: '',
        name: 'overview',
        component: () => import('@/views/overview/OverviewView.vue'),
        meta: { cap: 'overview', titleKey: 'nav.overview' },
      },
      {
        path: 'activation/quiz',
        name: 'quiz',
        component: () => import('@/views/activation/QuizView.vue'),
        meta: { cap: 'activation', titleKey: 'quiz.title' },
      },
      {
        // 零售认证预检（三步卡 + 5 行登记资料 + 黄条），确认后才进 SDK
        path: 'activation/kyc/start',
        name: 'kyc-start',
        component: () => import('@/views/activation/KycStartView.vue'),
        meta: { cap: 'activation', titleKey: 'kycStart.title' },
      },
      {
        path: 'activation/identity',
        name: 'kyc',
        component: () => import('@/views/activation/IdentityView.vue'),
        meta: { cap: 'activation', titleKey: 'kyc.title' },
      },
      {
        // 公司相关人士清单——开户前形态（任务清单）
        path: 'activation/people',
        name: 'kyc-people',
        component: () => import('@/views/activation/KycPeopleView.vue'),
        meta: { cap: 'activation', titleKey: 'kycPeople.titlePre', variant: 'pre' },
      },
      // `activation/mt5`（Mt5WaitView）已删：开通中 / 遇到问题自 09-20 起当场弹窗（GateDialogs），该页无入口且文案已与弹窗版分叉
      {
        path: 'accounts',
        name: 'my-account',
        component: () => import('@/views/account/MyAccountView.vue'),
        meta: { cap: 'accounts', titleKey: 'account.title' },
      },
      {
        path: 'profile',
        name: 'profile',
        component: () => import('@/views/account/ProfileView.vue'),
        meta: { cap: 'profile.read', titleKey: 'profile.title' },
      },
      {
        path: 'account/company',
        name: 'company-info',
        component: () => import('@/views/account/CompanyInfoView.vue'),
        meta: { cap: 'profile.read', titleKey: 'company.title' },
      },
      {
        /**
         * 同一个组件的**开户后形态**（常设的「人员认证与文件」）。
         * ⚠️ 与 `/activation/people` 复用组件时 RouterView 必须按 path 给 key，
         * 否则来回切换只换 meta 不重建，`variant` 读的是上一次的。
         */
        path: 'account/people',
        name: 'company-people',
        component: () => import('@/views/activation/KycPeopleView.vue'),
        meta: { cap: 'profile.read', titleKey: 'kycPeople.titleOpened', variant: 'opened' },
      },
      {
        path: 'funding/bank-accounts',
        name: 'bank-accounts',
        component: () => import('@/views/funding/BankAccountsView.vue'),
        meta: { cap: 'bank', titleKey: 'bank.title' },
      },
      {
        path: 'funding/deposit',
        name: 'deposit',
        component: () => import('@/views/funding/DepositView.vue'),
        meta: { cap: 'accounts', titleKey: 'deposit.title' },
      },
      {
        path: 'funding/withdraw',
        name: 'withdraw',
        component: () => import('@/views/funding/WithdrawView.vue'),
        meta: { cap: 'accounts', titleKey: 'withdraw.title' },
      },
      {
        // 报表两页共用一个组件，靠 meta.kind 区分（两张表的形状不同，但骨架一样）
        path: 'reports/deposits',
        name: 'deposit-records',
        component: () => import('@/views/reports/RecordsView.vue'),
        meta: { cap: 'accounts', titleKey: 'nav.depositRecords', kind: 'deposit' },
      },
      {
        path: 'reports/withdrawals',
        name: 'withdrawal-records',
        component: () => import('@/views/reports/RecordsView.vue'),
        meta: { cap: 'accounts', titleKey: 'nav.withdrawalRecords', kind: 'withdrawal' },
      },
      {
        path: 'platform',
        name: 'platform',
        component: () => import('@/views/platform/PlatformView.vue'),
        // 装客户端不需要先有账户，所以是 overview（BASE_ALWAYS）而不是 accounts，见 nav.ts 的说明
        meta: { cap: 'overview', titleKey: 'platform.title' },
      },
      {
        path: 'support',
        name: 'support',
        component: () => import('@/views/support/SupportView.vue'),
        // 唯一不设 cap 的登录后页面：帮助与支持任何状态都放行（§16）
        meta: { titleKey: 'support.title' },
      },
    ],
  },

  { path: '/:pathMatch(.*)*', redirect: '/' },
]

export const router = createRouter({
  history: createWebHistory(import.meta.env.VITE_BASE || '/'),
  routes,
  scrollBehavior: () => ({ top: 0 }),
})

router.beforeEach(async (to) => {
  const key = to.meta.titleKey
  document.title = key ? `${t(key)} · ${t('app.title')}` : t('app.title')

  if (to.meta.public) return true

  if (!tokenStore.get()) {
    return { name: 'login', query: redirectQueryOf(to.fullPath) }
  }

  const auth = useAuthStore()
  /**
   * 接口 2.0 起登录响应就带 portalMode / onboardingProgress，
   * 所以刚登录完 auth.ready 已经为真，不必再等一次 /profile/me（少一次白跑）。
   * 刷新页面时 session 丢了、token 还在，这时才需要拉 /profile/me 重建门禁。
   */
  if (!auth.ready) {
    try {
      await auth.fetchMe()
    } catch {
      // 拿不到就没法判门禁，一律回登录页重新走一遍。整套清干净——下一位登录的可能不是同一个人
      clearSession()
      return { name: 'login', query: redirectQueryOf(to.fullPath) }
    }
  }

  /**
   * 密码已过期 → 强制改密页，除该页外一律拦回（R-18）。
   * 接口层同样会拦（40301），前端这层只是省一次往返。
   */
  if (auth.passwordExpired && to.name !== 'change-password') {
    return { name: 'change-password' }
  }
  // 没过期却主动点进改密页是允许的（个人资料 → 安全 → 修改），带 self 标记区分
  if (!auth.passwordExpired && to.name === 'change-password' && !to.query.self) {
    return { path: '/' }
  }

  /**
   * 能力守卫。不满足时**回概览**并带上 blocked 参数，
   * 由概览页弹对应的拦截弹窗 —— 不做 403 页：客户看到 403 只会以为系统坏了。
   */
  const cap = to.meta.cap
  if (cap && !auth.can(cap)) {
    return { path: '/', query: { blocked: cap } }
  }

  return true
})

// 发版后旧页面加载不到新代码块 → 自动整页刷到目标地址（见 chunkReload.ts 头注释）
installChunkReload(router)
